import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X } from "lucide-react";

export const TargetSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const forYou = [
    "Tu es thérapeute / énergéticien(ne) / accompagnant(e) avec du fond",
    "Tu n'aimes pas l'IA, ou tu es sceptique, mais tu es prêt(e) à tester",
    "Tu veux être visible sans jouer un rôle",
    "Tu veux vendre mieux sans devenir un vendeur",
    "Tu n'as pas d'équipe, pas les moyens, et tu refuses d'attendre \"un jour\"",
    "Tu es prêt(e) à faire simple, régulier, incarné (même imparfait)",
  ];

  const notForYou = [
    "Tu veux \"des résultats sans t'engager\"",
    "Tu veux que l'IA fasse ta vie, tes posts, tes décisions à ta place",
    "Tu veux manipuler, forcer, promettre n'importe quoi",
    "Tu refuses toute structure (tu veux juste \"l'inspiration\")",
  ];

  return (
    <section 
      ref={ref}
      className="section-padding bg-white relative overflow-hidden"
      data-testid="target-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-32 h-32 border border-emerald-100 rounded-full opacity-50" />
      <div className="absolute bottom-20 right-10 w-48 h-48 border border-amber-100 rounded-full opacity-50" />

      <div className="lumen-container relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="font-cinzel text-sm tracking-[0.3em] text-gold-metallic uppercase mb-4 block">
              Pour qui ?
            </span>
            <h2 className="section-title font-cormorant text-text-main">
              À qui c'est destiné
            </h2>
          </motion.div>

          {/* Two columns */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* For you */}
            <motion.div
              className="glass-card p-8 border-emerald-100"
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-cormorant text-2xl text-emerald-700">
                  C'est pour toi si
                </h3>
              </div>

              <ul className="space-y-4">
                {forYou.map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  >
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="font-manrope text-text-main text-sm leading-relaxed">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Not for you */}
            <motion.div
              className="glass-card p-8 border-stone-200 bg-stone-50/50"
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                  <X className="w-5 h-5 text-stone-500" />
                </div>
                <h3 className="font-cormorant text-2xl text-text-muted">
                  Ce n'est pas pour toi si
                </h3>
              </div>

              <ul className="space-y-4">
                {notForYou.map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: 10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  >
                    <X className="w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5" />
                    <span className="font-manrope text-text-muted text-sm leading-relaxed">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Bottom note */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <p className="font-cormorant text-xl text-text-main">
              <span className="text-emerald-700 font-medium">LUMEN</span>, ce n'est pas un produit miracle.
              <br />
              <span className="italic text-text-muted">
                C'est un accélérateur de posture + d'expression.
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
